import { useEffect, useState } from "react";
import { Lock, X as XIcon, AlertTriangle } from "lucide-react";
import { Button } from "./ui/button";

/**
 * Password-protected confirmation dialog. Used for destructive merchant
 * actions (close a queue / delete a queue) per the security spec — the
 * establishment must re-enter their account password before the action
 * is performed.
 *
 * Props:
 *  - open: boolean
 *  - title, description, confirmLabel
 *  - destructive: when true, paints the confirm button in destructive red
 *  - onCancel(), onConfirm(password)
 *  - busy: disables the confirm button while the request is in-flight
 *  - errorMessage: optional, displayed in red under the input
 */
export default function PasswordConfirmDialog({
  open,
  title,
  description,
  confirmLabel = "Confirmer",
  destructive = false,
  onCancel,
  onConfirm,
  busy = false,
  errorMessage = "",
  testid = "password-confirm-dialog",
}) {
  const [pwd, setPwd] = useState("");
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!open) {
      setPwd("");
      setShow(false);
    }
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-ink/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onCancel}
      data-testid={testid}
    >
      <div
        className="relative w-full max-w-md bg-white rounded-2xl border border-border shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-5 py-3 border-b border-border flex items-center justify-between">
          <h2 className="font-display font-black uppercase tracking-[0.06em] text-base text-ink inline-flex items-center gap-2">
            <Lock size={16} className={destructive ? "text-destructive" : "text-brand"} /> {title}
          </h2>
          <button
            onClick={onCancel}
            className="text-subtle hover:text-ink"
            aria-label="Annuler"
            data-testid={`${testid}-close`}
          >
            <XIcon size={18} />
          </button>
        </header>
        <div className="p-5">
          {destructive && (
            <div className="mb-3 flex items-start gap-2 p-3 rounded-md bg-destructive/5 border border-destructive/30 text-destructive">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              <p className="text-xs leading-snug">
                <strong>Action irréversible.</strong> Cette action ne peut pas être annulée.
              </p>
            </div>
          )}
          {description && (
            <p className="text-sm text-ink leading-relaxed mb-3" data-testid={`${testid}-description`}>
              {description}
            </p>
          )}
          <label className="block text-[11px] font-bold uppercase tracking-wider text-subtle mb-1">
            Mot de passe de l'Établissement
          </label>
          <div className="relative">
            <input
              type={show ? "text" : "password"}
              autoFocus
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && pwd && !busy) onConfirm(pwd);
              }}
              placeholder="••••••••"
              className={`w-full h-11 px-3 pr-10 rounded-md border ${
                errorMessage ? "border-destructive" : "border-border"
              } bg-white text-ink font-mono outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand`}
              data-testid={`${testid}-input`}
            />
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-7 h-7 rounded text-subtle hover:text-ink text-[10px] font-bold uppercase"
              aria-label={show ? "Masquer" : "Afficher"}
              data-testid={`${testid}-toggle`}
            >
              {show ? "Masquer" : "Voir"}
            </button>
          </div>
          {errorMessage && (
            <p className="mt-2 text-xs text-destructive font-bold" data-testid={`${testid}-error`}>
              {errorMessage}
            </p>
          )}
          <div className="mt-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <Button
              variant="outline"
              onClick={onCancel}
              className="h-11 border border-border text-ink hover:bg-surface font-bold uppercase tracking-wider text-xs"
              data-testid={`${testid}-cancel-btn`}
            >
              Annuler
            </Button>
            <Button
              onClick={() => onConfirm(pwd)}
              disabled={busy || !pwd}
              className={`h-11 ${destructive ? "bg-destructive hover:bg-destructive/90" : "bg-brand hover:bg-brand-hover"} text-white font-bold uppercase tracking-wider text-xs disabled:opacity-50`}
              data-testid={`${testid}-confirm-btn`}
            >
              {busy ? "Vérification…" : confirmLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
